import type {
  DeleteDeckResponse,
  FlagDeckRequest,
  FlagDeckResponse,
  TeacherDeckDeletionsResponse,
} from '@study-decks/shared'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''

export class DeckModApiError extends Error {
  override readonly name = 'DeckModApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

export async function flagDeck(
  getIdToken: () => Promise<string>,
  deckId: string,
  body: FlagDeckRequest = {},
): Promise<FlagDeckResponse> {
  const idToken = await getIdToken()
  const response = await fetch(`${API_BASE_URL}/api/decks/${deckId}/flag`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${idToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    throw new DeckModApiError(
      errorBody.error?.code ?? 'REQUEST_FAILED',
      errorBody.error?.message ?? `Request failed (${response.status})`,
    )
  }

  return (await response.json()) as FlagDeckResponse
}

export async function deleteDeck(
  getIdToken: () => Promise<string>,
  deckId: string,
): Promise<DeleteDeckResponse> {
  const idToken = await getIdToken()
  const response = await fetch(`${API_BASE_URL}/api/decks/${deckId}`, {
    method: 'DELETE',
    headers: { authorization: `Bearer ${idToken}` },
  })

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    throw new DeckModApiError(
      errorBody.error?.code ?? 'REQUEST_FAILED',
      errorBody.error?.message ?? `Request failed (${response.status})`,
    )
  }

  return (await response.json()) as DeleteDeckResponse
}

export async function getTeacherDeckDeletions(
  getIdToken: () => Promise<string>,
  classId: string,
): Promise<TeacherDeckDeletionsResponse> {
  const idToken = await getIdToken()
  const response = await fetch(
    `${API_BASE_URL}/api/decks/deletions?classId=${encodeURIComponent(classId)}`,
    { headers: { authorization: `Bearer ${idToken}` } },
  )

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    throw new DeckModApiError(
      errorBody.error?.code ?? 'REQUEST_FAILED',
      errorBody.error?.message ?? `Request failed (${response.status})`,
    )
  }

  return (await response.json()) as TeacherDeckDeletionsResponse
}
